import DesignServicesRoundedIcon from "@mui/icons-material/DesignServicesRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import TerminalRoundedIcon from "@mui/icons-material/TerminalRounded";
import SpeedRoundedIcon from "@mui/icons-material/SpeedRounded";
import VolunteerActivismRoundedIcon from "@mui/icons-material/VolunteerActivismRounded";
import SecurityRoundedIcon from "@mui/icons-material/SecurityRounded";
import PrecisionManufacturingRoundedIcon from "@mui/icons-material/PrecisionManufacturingRounded";
import LightbulbRoundedIcon from "@mui/icons-material/LightbulbRounded";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";
import { SvgIconComponent } from "@mui/icons-material";
import generateConfig from "../../util/config";

const config = generateConfig();
const contactEmail = config.contactEmail;

export type Highlight = {
  title: string;
  description: string;
  icon: SvgIconComponent;
};

export type Project = {
  title: string;
  description: string;
  stack: string[];
  action: {
    href: string;
    label: string;
  };
};

export type Experience = {
  period: string;
  role: string;
  summary: string;
};

export const highlights: Highlight[] = [
  {
    title: "Human-centred design",
    description:
      "Crafting interfaces that balance aesthetics with delightful, accessible interactions across devices.",
    icon: DesignServicesRoundedIcon,
  },
  {
    title: "Production-ready code",
    description:
      "Shipping resilient front-end architectures with TypeScript, React Query, and tested design systems.",
    icon: TerminalRoundedIcon,
  },
  {
    title: "Performance obsessed",
    description:
      "Auditing bundle weight, leveraging modern tooling, and tuning UX flows to keep experiences fast.",
    icon: SpeedRoundedIcon,
  },
];

export const pillars: Highlight[] = [
  {
    title: "AI-enhanced workflows",
    description:
      "Integrating conversational agents and smart tooling so products feel anticipatory, not reactive.",
    icon: AutoAwesomeRoundedIcon,
  },
  {
    title: "Inclusive experiences",
    description:
      "Accessibility-first mindset and motion design patterns that welcome every visitor.",
    icon: VolunteerActivismRoundedIcon,
  },
  {
    title: "Secure foundations",
    description:
      "Authentication, authorisation, and data flows built with privacy and trust top-of-mind.",
    icon: SecurityRoundedIcon,
  },
];

export const projects: Project[] = [
  {
    title: "Portfolio platform evolution",
    description:
      "A modular portfolio template with content APIs, chat integrations, and real-time analytics.",
    stack: ["Next.js", "TypeScript", "MUI", "React Query"],
    action: {
      href: "https://github.com/krushna-raut",
      label: "View GitHub",
    },
  },
  {
    title: "Conversational support assistant",
    description:
      "Gemini-powered chat assistant that shortens response times while keeping a personal tone.",
    stack: ["Google Gemini", "Redux", "Serverless"],
    action: {
      href: "/chatbot",
      label: "Try the demo",
    },
  },
  {
    title: "Design system accelerator",
    description:
      "Reusable component library with theming, tokens, and motion guidelines for rapid product teams.",
    stack: ["Storybook", "MUI", "Figma", "Framer Motion"],
    action: {
      href: `mailto:${contactEmail}`,
      label: "Request access",
    },
  },
];

export const experience: Experience[] = [
  {
    period: "2021 - Present",
    role: "Senior Front-end Engineer",
    summary:
      "Leading web experience initiatives, scaling design systems, and mentoring teams on accessibility.",
  },
  {
    period: "2018 - 2021",
    role: "Full-stack Developer",
    summary:
      "Built end-to-end web products with Node.js and React, focusing on resilient architecture and DevOps.",
  },
  {
    period: "2016 - 2018",
    role: "UI/UX Engineer",
    summary:
      "Partnered with designers to experiment quickly, validate ideas, and ship polished MVPs.",
  },
];

export const credBadges = [
  {
    icon: PrecisionManufacturingRoundedIcon,
    label: "Custom automation",
  },
  {
    icon: LightbulbRoundedIcon,
    label: "Strategic ideation",
  },
  {
    icon: WorkspacePremiumRoundedIcon,
    label: "Quality obsessed",
  },
];
